import { useState, useMemo, useEffect } from 'react';
import { Modal, Form, Button, Alert, Table } from 'react-bootstrap';
import { redisFetch } from '../../features/jobs/api/http';

// Board for swapping / re-attributing FINISHED (DONE) drums between products.
// Each produced drum gets a "belongs to product" select (+ relaunch to redo it as
// PENDING). Changed rows are sent as a batch to reallocate-produced (atomic swaps).
const ReallocationWizardModal = ({
  show,
  onHide,
  logs = [],
  machineId,
  // Products picked in Execution History that produced nothing on the day on
  // screen. The target list below is built from the drums in view, so without
  // these a drum cannot be moved to a product from another day — which is most
  // of them, once a run has slipped past midnight.
  extraProducts = [],
  onDone
}) => {
  const drums = useMemo(
    () =>
      (logs || [])
        .filter(l => !l.cancelled)
        .map(l => ({
          jobId: String(l.jobId || ''),
          productLabel: l.productLabel || l.jobId,
          taskId: l.taskId,
          tokenId: l.tokenId,
          color: l.color,
          plannedLength: l.plannedLength
        })),
    [logs]
  );

  const productOptions = useMemo(() => {
    const seen = new Map();
    for (const d of drums) if (!seen.has(d.jobId)) seen.set(d.jobId, d.productLabel);
    for (const p of extraProducts) {
      if (p?.jobId && !seen.has(String(p.jobId))) {
        seen.set(String(p.jobId), p.productLabel || p.jobId);
      }
    }
    return [...seen.entries()].map(([jobId, productLabel]) => ({ jobId, productLabel }));
  }, [drums, extraProducts]);

  const [assign, setAssign] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (show) {
      const init = {};
      drums.forEach(d => {
        init[d.tokenId] = { targetJobId: d.jobId, relaunch: false };
      });
      setAssign(init);
      setPreview(null);
      setErr(null);
    }
  }, [show, drums]);

  const set = (tok, patch) => {
    setAssign(a => ({ ...a, [tok]: { ...a[tok], ...patch } }));
    setPreview(null);
  };

  const changedItems = drums
    .map(d => {
      const a = assign[d.tokenId] || {};
      const changed = (a.targetJobId && a.targetJobId !== d.jobId) || a.relaunch;
      return changed
        ? {
            sourceJobId: d.jobId,
            taskId: d.taskId,
            tokenId: d.tokenId,
            targetJobId: a.targetJobId || d.jobId,
            relaunch: !!a.relaunch
          }
        : null;
    })
    .filter(Boolean);

  const run = async dryRun => {
    setErr(null);
    setBusy(true);
    try {
      const res = await redisFetch(
        `/api/redis/queues/${machineId}/reallocate-produced`,
        {
          method: 'POST',
          body: JSON.stringify({ items: changedItems, dryRun })
        }
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      if (dryRun) setPreview(d);
      else onDone?.();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">Reallocate produced drums</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fs-9 text-700 mb-2">
          For each produced drum set which product it belongs to (or relaunch it as
          PENDING). Same or different colors, any length. DONE drums stay finished —
          the history follows the product.
        </div>
        {drums.length === 0 ? (
          <div className="text-center py-4 text-700 fs-9">
            No produced drums on this day.
          </div>
        ) : (
          <Table responsive hover className="fs-9 mb-2">
            <thead>
              <tr>
                <th>Produs curent</th>
                <th>Culoare</th>
                <th className="text-end">Lungime</th>
                <th>Token</th>
                <th>Belongs to →</th>
                <th className="text-center">Relaunch</th>
              </tr>
            </thead>
            <tbody>
              {drums.map(d => {
                const a = assign[d.tokenId] || {};
                const changed = (a.targetJobId && a.targetJobId !== d.jobId) || a.relaunch;
                return (
                  <tr key={d.tokenId} className={changed ? 'table-warning' : ''}>
                    <td>{d.productLabel}</td>
                    <td>{d.color || '-'}</td>
                    <td className="text-end">{d.plannedLength}m</td>
                    <td className="font-monospace text-700">{d.tokenId}</td>
                    <td style={{ minWidth: 160 }}>
                      <Form.Select
                        size="sm"
                        value={a.targetJobId || d.jobId}
                        onChange={e => set(d.tokenId, { targetJobId: e.target.value })}
                      >
                        {productOptions.map(p => (
                          <option key={p.jobId} value={p.jobId}>
                            {p.productLabel}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={!!a.relaunch}
                        onChange={e => set(d.tokenId, { relaunch: e.target.checked })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        {err && (
          <Alert variant="danger" className="fs-9">
            {err}
          </Alert>
        )}
        {preview && (
          <Alert variant="info" className="fs-9 mb-0">
            <div className="fw-semibold mb-1">Preview (nothing written yet):</div>
            {(preview.moved || []).map((m, i) => (
              <div key={i}>
                → {m.color} {m.plannedLength}m: <b>{m.from}</b> → <b>{m.to}</b>
                {m.relaunch ? ' (relaunch → queue)' : ''}
              </div>
            ))}
            {(preview.warnings || []).length ? (
              <div className="text-warning">{preview.warnings.join('; ')}</div>
            ) : null}
            <div className="text-700 fs-10 mt-1">
              DONE drums stay finished (history follows product); queue untouched
              unless relaunch.
            </div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="me-auto fs-9 text-700">{changedItems.length} change(s)</div>
        <Button variant="link" className="text-600" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="outline-primary"
          disabled={busy || !changedItems.length}
          onClick={() => run(true)}
        >
          {busy ? '...' : 'Preview'}
        </Button>
        <Button
          variant="primary"
          disabled={busy || !changedItems.length || !preview}
          onClick={() => run(false)}
        >
          {busy ? 'Applying...' : 'Apply'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReallocationWizardModal;
